import React, { useContext } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import moment from "moment-timezone";
import { orderStatuses, shippingStatuses } from "../../../constants/shipping";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ToasterContext } from "../../../components/shared/Toaster/ToasterContext";
import { NotificationType, SERVER_URL } from "../../../constants/global";
import { useMutationUpdateOrder } from "../../../queries/order/hooks/useMutationUpdateOrder";
import { CircularProgress } from "@mui/material";
import Link from "@mui/material/Link";
import { grey } from "@mui/material/colors";
import { OrderType, OrderUserType } from "../../../constants/order";
import { Item } from "../../../components/shared/Item/Item";
import Grid from "@mui/material/Grid";
import EditOrder from "./EditOrder";

interface OrderItemProps {
  type: OrderType.openOrder | OrderType.history;
  user?: OrderUserType.buyer | OrderUserType.seller;
  item: any;
  action?: (event: any) => void;
  refetch?: any;
}

export const OrderItem: React.FunctionComponent<OrderItemProps> = ({ type, user = OrderUserType.seller, item, action, refetch }) => {
  const { addToast } = useContext(ToasterContext);
  const [updateOrder, { loading: updateLoading }] = useMutationUpdateOrder();
  const [editOrderID, setEditOrderID] = React.useState<string | null>(null);

  const handleBuyerConfirm = (event: any) => {
    event.stopPropagation();
    updateOrder({
      variables: {
        data: { shippingStatus: { set: 5 } },
        where: { id: item.id },
      },
    })
      .then(() => {
        refetch();
      })
      .catch((error: any) => {
        addToast({ type: NotificationType.Error, title: error.message });
      });
  };

  return (
    <>
      {editOrderID && <EditOrder editOrderID={editOrderID} setEditOrderID={setEditOrderID} />}

      <Paper sx={type === OrderType.openOrder ? {} : { cursor: "pointer", ":hover": { backgroundColor: grey[50] } }}>
        <Box sx={{ p: 2, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} xl={2}>
              <Item label="Order Number" value={item.orderNumber} column>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {item.orderNumber}
                  <ContentCopyIcon
                    onClick={(event: any) => {
                      event.stopPropagation();
                      navigator.clipboard.writeText(item.orderNumber);
                      addToast({ type: NotificationType.Success, title: "Copied to Clipboard" });
                    }}
                    fontSize="small"
                    sx={{ ml: 1, cursor: "pointer" }}
                  />
                </Box>
              </Item>
            </Grid>
            <Grid item xs={12} sm={6} xl={2}>
              <Item label="Order Date" value={moment(item.createdAt).format("MM/DD/YYYY hh:mm A")} column />
            </Grid>
            <Grid item xs={12} sm={6} xl={2}>
              <Item label="Shipping Method" value={item.ShippingMethod.name} column />
            </Grid>
            <Grid item xs={12} sm={6} xl={2}>
              <Item labelSX={{ maxWidth: "220px", textAlign: "center" }} label="Shipping Status" value={shippingStatuses[item.shippingStatus].label} column>
                <Box
                  sx={{
                    maxWidth: "220px",
                    textAlign: "center",
                    fontSize: "12px",
                    borderRadius: "16px",
                    color: shippingStatuses[item.shippingStatus].color,
                    backgroundColor: shippingStatuses[item.shippingStatus].bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pl: 2,
                    pr: 2,
                    pt: 0.5,
                    pb: 0.5,
                  }}
                >
                  {shippingStatuses[item.shippingStatus].label}
                </Box>
              </Item>
            </Grid>
            <Grid item xs={12} sm={6} xl={2}>
              {type === OrderType.history && (
                <Item label="Status" value={orderStatuses[item.status].label} column>
                  <Box
                    sx={{
                      maxWidth: "150px",
                      fontSize: "12px",
                      borderRadius: "16px",
                      color: orderStatuses[item.status].color,
                      backgroundColor: orderStatuses[item.status].bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pl: 2,
                      pr: 2,
                      pt: 0.5,
                      pb: 0.5,
                    }}
                  >
                    {orderStatuses[item.status].label}
                  </Box>
                </Item>
              )}
            </Grid>
            <Grid item xs={12} sm={6} xl={2}>
              <Item label="Credit Total" value={item.totalAmount} hasAmount column />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} md={4} xl={6}>
              {item.trackingNumber && (
                <Item label="Tracking ID" value={orderStatuses[item.status].label} column>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {item.trackingNumber}
                    <ContentCopyIcon
                      onClick={(event: any) => {
                        event.stopPropagation();
                        navigator.clipboard.writeText(item.trackingNumber);
                        addToast({ type: NotificationType.Success, title: "Copied to Clipboard" });
                      }}
                      fontSize="small"
                      sx={{ ml: 1, cursor: "pointer" }}
                    />
                  </Box>
                </Item>
              )}
            </Grid>
            <Grid item xs={12} md={8} xl={6}>
              {type === OrderType.openOrder && (
                <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
                  {type === OrderType.openOrder && (
                    <>
                      {/* {item.loopStatus === "pending_seller" && (
                        <Button
                          sx={{ width: "200px", mr: 2 }}
                          color="primary"
                          variant="contained"
                          onClick={() => {
                            setEditOrderID(item.orderNumber);
                          }}
                        >
                          Edit Order
                        </Button>
                      )} */}

                      <Button sx={{ width: "200px", mr: 2 }} color="primary" variant="contained" onClick={(event: any) => (action ? action(event) : undefined)}>
                        Confirm Order
                      </Button>
                    </>
                  )}
                </Box>
              )}
              {type === OrderType.history && (
                <Grid container spacing={2} sx={{ mt: 3, display: "flex", justifyContent: "end" }}>
                  {type === OrderType.history && user === OrderUserType.seller && item.shippingStatus === 1 && item.CheckList && (
                    <Grid item xs={12} md={4}>
                      <Link href={`${SERVER_URL}/file/${item.CheckList.key}`} target="_blank">
                        <Button fullWidth sx={{ mr: 2 }} color="primary" variant="contained" onClick={(event: any) => event.stopPropagation()}>
                          Print Checklist
                        </Button>
                      </Link>
                    </Grid>
                  )}
                  {type === OrderType.history && user === OrderUserType.seller && item.shippingStatus === 1 && item.Label && (
                    <Grid item xs={12} md={4}>
                      <Link href={`${SERVER_URL}/file/${item.Label.key}`} target="_blank">
                        <Button fullWidth sx={{ mr: 2 }} color="primary" variant="contained" onClick={(event: any) => event.stopPropagation()}>
                          Print Shipping Label
                        </Button>
                      </Link>
                    </Grid>
                  )}
                  {item.shippingStatus === 4 && user === OrderUserType.buyer && (
                    <Grid item xs={12} md={4}>
                      <Button sx={{ mr: 2 }} color="primary" variant="contained" onClick={(event: any) => handleBuyerConfirm(event)} fullWidth>
                        Confirm Order
                        {updateLoading && <CircularProgress sx={{ ml: 2 }} size={14} color="inherit" />}
                      </Button>
                    </Grid>
                  )}
                  {item.trackingNumber && (
                    <Grid item xs={12} md={4}>
                      <Button
                        color="primary"
                        variant="contained"
                        fullWidth
                        onClick={(event: any) => {
                          event.stopPropagation();
                          window.open(`https://www.fedex.com/fedextrack/?trknbr=${item.trackingNumber}`, "_blank");
                        }}
                      >
                        Track Order
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </>
  );
};
